import "./index.css";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Table,
  Button,
  message,
  Space,
  Row,
  Col,
  Statistic,
  Tag,
  Input,
  Select,
  Spin,
  Tooltip,
  Modal,
} from "antd";
import {
  UserOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ExportOutlined,
  SearchOutlined,
  FilterOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import MenuLayout from "../../../components/Menu";
import Loading from "../../../components/Loading";
import { useUser } from "../../../contexts/UserContext";
import { supabase } from "../../../lib/supabase";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const NullCoordinatesPage = () => {
  const navigate = useNavigate();
  const { userProfile, isAdmin, loading, retryCount } = useUser();
  const [usersData, setUsersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate("/search");
    }
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsersWithNullCoordinates();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterAndSearchData();
  }, [usersData, searchText, filterType]);

  // Helper function to check coordinate validity
  const getCoordinateStatus = (user) => {
    if (!user.loc_coord) {
      return { status: "null", text: "无坐标数据", type: "error" };
    }
    if (user.loc_coord.lat === null || user.loc_coord.lon === null) {
      return { status: "invalid", text: "坐标无效", type: "warning" };
    }
    return { status: "valid", text: "坐标有效", type: "success" };
  };

  const fetchUsersWithNullCoordinates = async () => {
    setDataLoading(true);
    try {
      // Use the exact same API call structure as the map page with proper numeric handling
      const queryParams = {
        q_name: null,
        q_loc: null,
        q_school: null,
        q_major: null,
        q_schoolm_start_time: null,
        q_schoolm_class: null,
        q_schoolh_start_time: null,
        q_schoolh_class: null,
        q_career: null,
        q_job: null,
        page: 1,
        qsize: 2000, // Get a large number to include all users
      };

      const { data, error } = await supabase.rpc("query_filter_map", queryParams);

      if (error) {
        console.error("API Error:", error);
        message.error("加载用户数据失败");
        return;
      }

      const allUsers = data?.data || [];
      
      // Filter users with coordinate issues
      const problematicUsers = allUsers.filter(user => {
        const coordStatus = getCoordinateStatus(user);
        return coordStatus.status === "null" || coordStatus.status === "invalid";
      });

      setUsersData(problematicUsers);
      setPagination(prev => ({
        ...prev,
        total: problematicUsers.length,
      }));
    } catch (err) {
      console.error("Fetch Error:", err);
      message.error("加载数据时发生错误");
    } finally {
      setDataLoading(false);
    }
  };

  const filterAndSearchData = () => {
    let filtered = [...usersData];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(user => 
        user.name_cn?.toLowerCase().includes(searchLower) ||
        user.name_pinyin?.toLowerCase().includes(searchLower) ||
        user.loc_country?.toLowerCase().includes(searchLower) ||
        user.loc_city?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(user => {
        const coordStatus = getCoordinateStatus(user);
        return coordStatus.status === filterType;
      });
    }

    setFilteredData(filtered);
    setPagination(prev => ({
      ...prev,
      current: 1,
      total: filtered.length,
    }));
  };

  const handleExportData = () => {
    try {
      const exportData = filteredData.map(user => ({
        姓名: user.name_cn,
        拼音: user.name_pinyin,
        国家: user.loc_country || "未填写",
        城市: user.loc_city || "未填写",
        坐标状态: getCoordinateStatus(user).text,
        坐标数据: user.loc_coord ? JSON.stringify(user.loc_coord) : "无",
        初中学校: user.schoolm,
        入学年份: user.schoolm_start_time,
        班级: user.schoolm_class,
      }));

      const csvContent = [
        Object.keys(exportData[0]).join(","),
        ...exportData.map(row => Object.values(row).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `null_coordinates_users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("数据导出成功");
    } catch (err) {
      message.error("导出数据失败");
    }
  };

  const handleFixCoordinates = (record) => {
    Modal.confirm({
      title: "修复坐标数据",
      icon: <ExclamationCircleOutlined />,
      content: `确定要尝试修复 ${record.name_cn} 的坐标数据吗？系统将基于其填写的国家和城市信息重新获取坐标。`,
      okText: "确定修复",
      cancelText: "取消",
      onOk: async () => {
        try {
          // This would trigger a coordinate update in the backend
          message.info("坐标修复功能需要后端支持，请联系技术人员");
        } catch (err) {
          message.error("修复失败");
        }
      },
    });
  };

  const columns = [
    {
      title: "姓名",
      dataIndex: "name_cn",
      key: "name_cn",
      width: 120,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.name_pinyin}
          </Text>
        </Space>
      ),
    },
    {
      title: "学校信息",
      key: "school_info",
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.schoolm}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.schoolm_start_time}级 {record.schoolm_class}班
          </Text>
        </Space>
      ),
    },
    {
      title: "位置信息",
      key: "location_info",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text className={!record.loc_country && !record.loc_city ? "location-info-missing" : ""}>
            {record.loc_country || "未填写"} {record.loc_city || "未填写"}
          </Text>
          {(!record.loc_country || !record.loc_city) && (
            <Text type="secondary" style={{ fontSize: "12px", color: "#ff4d4f" }}>
              位置信息不完整
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "坐标状态",
      key: "coordinate_status",
      width: 150,
      render: (_, record) => {
        const status = getCoordinateStatus(record);
        return (
          <div className="coordinates-status">
            {status.status === "null" && <ExclamationCircleOutlined className="status-null" />}
            {status.status === "invalid" && <WarningOutlined className="status-invalid" />}
            <Tag color={status.type === "error" ? "red" : "orange"}>
              {status.text}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "坐标数据",
      key: "coordinate_data",
      width: 180,
      render: (_, record) => (
        <Text code style={{ fontSize: "12px" }}>
          {record.loc_coord ? JSON.stringify(record.loc_coord) : "null"}
        </Text>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="修复坐标">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleFixCoordinates(record)}
              disabled={!record.loc_country || !record.loc_city}
            >
              修复
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  if (!loading && !isAdmin) {
    return null;
  }

  const nullCoordUsers = filteredData.filter(user => getCoordinateStatus(user).status === "null");
  const invalidCoordUsers = filteredData.filter(user => getCoordinateStatus(user).status === "invalid");

  return (
    <MenuLayout>
      <Spin 
        tip={retryCount > 0 ? `正在重试... (第 ${retryCount} 次尝试)` : "加载中..."} 
        indicator={<Loading />} 
        spinning={loading}
      >
        <div className="null-coordinates-admin-page">
          <Card className="null-coordinates-admin-card">
            <div className="null-coordinates-header">
              <div>
                <Title level={2}>
                  <EnvironmentOutlined /> 坐标异常用户管理
                </Title>
                <Text type="secondary">
                  管理和修复没有有效坐标数据的用户
                </Text>
              </div>
              <div className="export-actions">
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchUsersWithNullCoordinates}
                  loading={dataLoading}
                >
                  刷新数据
                </Button>
                <Button 
                  icon={<ExportOutlined />} 
                  onClick={handleExportData}
                  disabled={filteredData.length === 0}
                >
                  导出数据
                </Button>
              </div>
            </div>

            {/* Statistics */}
            <Row gutter={[16, 16]} className="null-coordinates-stats">
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="总异常用户"
                    value={filteredData.length}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="无坐标数据"
                    value={nullCoordUsers.length}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="坐标无效"
                    value={invalidCoordUsers.length}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Filters */}
            <div className="filter-section">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <Search
                    placeholder="搜索姓名、位置..."
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="筛选类型"
                    value={filterType}
                    onChange={setFilterType}
                    prefix={<FilterOutlined />}
                  >
                    <Option value="all">全部异常</Option>
                    <Option value="null">无坐标数据</Option>
                    <Option value="invalid">坐标无效</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Text type="secondary">
                    显示 {filteredData.length} / {usersData.length} 个用户
                  </Text>
                </Col>
              </Row>
            </div>

            {/* Table */}
            <Table
              className="null-coordinates-table"
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              loading={dataLoading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                },
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        </div>
      </Spin>
    </MenuLayout>
  );
};

export default NullCoordinatesPage;
